import Events from "../utils/Events.js";
import NuCalBaseView from "./NuCalBaseView.js";
import {getNextMonth,getPreviousMonth, getDaysInNextMonth, getMonthPadding, getDaysInMonth} from  "../utils/General.js";

/**
 * @extends NuCalBaseView
 */
class NuCalSingleCalendarView extends NuCalBaseView {

	/**
    * @param {Object} props properties.
    * @param {String} props.namespace The namespace in which this object should subscribe and publish events on.
    * @param {HTMLElement} props.view The HTML Element in which this object should render the output too.
    * @param {String} props.dateType Which date the object is dealing with. Can be "arrival", "departure" or "both". Default is "both".
    * @param {String} props.locale Language code. Default "en-US."
    * @param {String} props.UUID Unique Identifier. the default is randomly generated.
    * @param {Function} props.onDateSelect A callback for when a date is selected.
    * @param {String} props.classPrefix A custom class prefix.
    */
	constructor(props){
		super(props);
		this.onDateSelect = props.onDateSelect;
		this.classPrefix = props.classPrefix || "cp-nuCalSingleCalendar";
        
		this.view.classList.add(this.classPrefix);
		this.view.innerHTML = `
            <div class="${this.classPrefix}-calendar"></div>
        `;
		this.selectedDate = new Date();

	}

	/**
     * The selected Date depending on what `dateType` is set too.
     * @type {Date}.
    */
	get selectedDate(){
		return this._selectedDate;
	}

	set selectedDate(d){
		if(d instanceof Date){
			this._selectedDate = d;
			this._selectedDate.setHours(0,0,0,0);
			const dateChangeEvent = (this.dateType === "arrival")? this._events.updateArrivalDate : this._events.updateDepartureDate;
			Events.publish(dateChangeEvent,{ "date": this._selectedDate });
			this.onDateSelect(d);
		}
	}

	/**
     * The focused Date, but not the actually selected one. This allows us to be looking
     * at another month or year without committing to it.
     * @type {Date}.
    */
	get focusDate(){
		return this._focusDate;
	}

	set focusDate(d){
		if(d instanceof Date){
			const oldFocusDate = this._focusDate;
			this._focusDate = d;
			this._focusDate.setHours(0,0,0,0);

			const focusClass = `${this.classPrefix}-cell--focused`;

			if(this._focusDate.valueOf() < this.minDate.valueOf()){
				this._focusDate = new Date(this.minDate.valueOf());
			}
			if(this._focusDate.valueOf() > this.maxDate.valueOf()){
				this._focusDate = new Date(this.maxDate.valueOf());
			}

			if( //Do we need to redraw calendar or just update the elements?
				this._focusDate.getMonth() === oldFocusDate.getMonth() &&
                this._focusDate.getFullYear() === oldFocusDate.getFullYear()
			){
				//Just update the elements
				this.view.querySelectorAll(`.${focusClass}`).forEach(el => {
					el.classList.remove(focusClass);
					el.querySelector("button").tabIndex = -1;
				});
			}else{ //redraw
				this.render();
				return; //render will recall this method to handle focus when we're done
			}
            
			const newFocus = this.getFocusDateElement();
			if(newFocus === null){
				return;
			}
			newFocus.classList.add(focusClass);
			newFocus.querySelector("button").tabIndex = 0;
		}
	}

	/**
     *  returns the focused date cell.
     * @type {HTMLElement}
     */

	getFocusDateElement(){
		return this.view.querySelector(`.${this.classPrefix}-cell[data-date="${this._focusDate.getDate()}"]`);
	}

	/**
     * focuses the previous month.
     */
	focusPrevMonth(){
		this.focusDate = getPreviousMonth(this.focusDate);
	}

	/**
     * focuses the next month.
     */
	focusNextMonth(){
		this.focusDate = getNextMonth(this.focusDate);
	}

	subscribe(){
		this._subscriptions.push(Events.subscribe(this._events.datesUpdated,this.update.bind(this)));
	}

	/**
     * A callback for when a date is selected.
     * @type {function}
     */
	get onDateSelect(){
		return this._onDateSelect;
	}
    
	set onDateSelect(f){
		this._onDateSelect = (typeof f === "function")? f : function(){};
	}

	_buildEvents(){
		NuCalBaseView.prototype._buildEvents.call(this);
		let n = this._namespace;
		let id = this.UUID;
		if(typeof n == "string" && n !== ""){
			n = "/" + n;
		}else{
			n = "";
		}
		if(typeof id == "string" && id !== ""){
			id = "/" + id;
		}else{
			id = "";
		}
		this._events.calendarDayClicked = `/nuCal${n}${id}/calendarDayClicked`;
	}

	update(props){
		if(this.dateType === "arrival"){
			this._selectedDate = props.arrivalDate;
			this._focusDate = props.arrivalDate;
			this.minDate = (props.minArrivalDate !== null)? new Date(props.minArrivalDate.valueOf()) : null;
			this.maxDate = (props.maxArrivalDate !== null)? new Date(props.maxArrivalDate.valueOf()) : null;
		}else{
			this._selectedDate = props.departureDate;
			this._focusDate = props.departureDate;
			this.minDate = (props.minDepartureDate !== null)? new Date(props.minDepartureDate.valueOf()) : null;
			this.maxDate = (props.maxDepartureDate !== null)? new Date(props.maxDepartureDate.valueOf()) : null;
		}
		this.render();
	}

	render(){

		const prevMonth = getPreviousMonth(this.focusDate);
		const nextMonth = getNextMonth(this.focusDate);

		const lexi = this.lexicon.NuCalSingleCalendarView;
		console.log(lexi);

		//build visual calendar
		this.view.querySelector(`.${this.classPrefix}-calendar`).innerHTML = `
        <div class="${this.classPrefix}-header">
            <div class="${this.classPrefix}-monthArrowWrapper--prev"><button type="button" class="${this.classPrefix}-monthArrow ${this.classPrefix}-monthArrow--prev"
            aria-label="${lexi.previousLabelAria}. ${ prevMonth.toLocaleDateString(this.locale,{ month: "long", year: "numeric" })}">${lexi.previousLabel}</button></div>
            <div class="${this.classPrefix}-title">${this.focusDate.toLocaleDateString(this.locale,{ month: "short", year: "numeric" })}</div>
            <div class="${this.classPrefix}-monthArrowWrapper--next"><button type="button" class="${this.classPrefix}-monthArrow ${this.classPrefix}-monthArrow--next"
            aria-label="${lexi.nextLabelAria} ${ nextMonth.toLocaleDateString(this.locale,{ month: "long", year: "numeric" })}">${lexi.nextLabel}</button></div>
        </div>`;
		this.view.querySelector(`.${this.classPrefix}-calendar`).appendChild(this._buildTable());
        
		this._prevMonthButton = this.view.querySelector(`.${this.classPrefix}-monthArrow--prev`);
		this._nextMonthButton = this.view.querySelector(`.${this.classPrefix}-monthArrow--next`);


		if(this.minDate.getFullYear() === this.focusDate.getFullYear() && this.minDate.getMonth() === this.focusDate.getMonth() ){
			this._prevMonthButton.disabled = true;
		}

		if(this.maxDate.getFullYear() === this.focusDate.getFullYear() && this.maxDate.getMonth() === this.focusDate.getMonth() ){
			this._nextMonthButton.disabled = true;
		}

		this._prevMonthButton.addEventListener("click",(e) => {
			e.preventDefault();
			this.focusPrevMonth();
			this._prevMonthButton.focus();
		});

		this._nextMonthButton.addEventListener("click",(e) => {
			e.preventDefault();
			this.focusNextMonth();
			this._nextMonthButton.focus();
		});

		this.focusDate = this.focusDate; //refocus

	}

	_buildTable () {
		const daysInMonth = new Date(this.focusDate.getFullYear(), this.focusDate.getMonth()+1, 0).getDate();
		const dayOffset = getMonthPadding(this.focusDate);
		const firstDayOfMonth = new Date(this.focusDate.getFullYear(), this.focusDate.getMonth(), 1);

		let playDate; //this is for playing with and getting formatting from.
		playDate = new Date(this.focusDate.getFullYear(),this.focusDate.getMonth(),1  - dayOffset);

		let calRow = null;
		const calTable = document.createElement("table");
		calTable.classList.add(`.${this.classPrefix}-dates`);
		calTable.setAttribute("role","presentation");
		//TABLE HEADER
		let calHeader = document.createElement("tr");
		//Set to Sunday
		playDate = new Date();
		playDate.setDate( playDate.getDate() - playDate.getDay() ); //set to a sunday
		for(let i = 0; i < 7; i++){
			const thCell = document.createElement("th");
			thCell.innerHTML = playDate.toLocaleDateString(this.locale,{ weekday: "short" });
			calHeader.appendChild(thCell);
			playDate.setDate(playDate.getDate() + 1);
		}
		calTable.appendChild(calHeader);


		//table Cells
		for(let i = 1; i <= (dayOffset + daysInMonth); i++ ){
			//create a row when needed
			if((i-1) % 7 === 0){
				if(calRow !== null){
					calTable.appendChild(calRow);
				}
				calRow = document.createElement("tr");
			}

			playDate = new Date(this.focusDate.getFullYear(),this.focusDate.getMonth(),i - dayOffset);
			const cellContent = playDate.toLocaleDateString(this.locale,{ "day": "2-digit" });
			const cell = document.createElement("td");
			cell.classList.add(`${this.classPrefix}-cell`);

			if(playDate.valueOf() <  firstDayOfMonth.valueOf()){ //padding
				cell.classList.add(`${this.classPrefix}-cell--disabled`);
				cell.classList.add(`${this.classPrefix}-cell--padding`);
				calRow.appendChild(cell);
				continue;
			}else{ //date days
				if(playDate.valueOf() < this.minDate.valueOf() || playDate.valueOf() > this.maxDate.valueOf()){ //disabled date days
					cell.classList.add(`${this.classPrefix}-cell--disabled`);
					const button = document.createElement("button");
					button.disabled = true;
					button.innerHTML = cellContent;
					cell.appendChild(button);
					calRow.appendChild(cell); 
					continue;
				}else{ //active days
                    
					cell.dataset.date = playDate.getDate();
					cell.dataset.month = playDate.getMonth() + 1;
					cell.dataset.year = playDate.getFullYear();

					if(playDate.valueOf() === this.selectedDate.valueOf()){
						cell.classList.add(`${this.classPrefix}-cell--selected`);                    
					}
					const button = document.createElement("button");
					button.innerHTML = cellContent;
					button.setAttribute("aria-label",playDate.toLocaleDateString(this.locale,{weekday: "long", year: "numeric", month: "long", day: "numeric"}));
					button.tabIndex = -1;
					let d = new Date(playDate.getFullYear(),playDate.getMonth(),playDate.getDate());
					let columnNo = d.getDay();
					let rowNo = calTable.childElementCount;
					button.addEventListener("click",(e) => {
						e.preventDefault();
						Events.publish(this._events.calendarDayClicked,{ "date": d });
						this.selectedDate = d;
						this.setFocusDateAndFocus(d);
					});
					const prevMonthPadding = getMonthPadding(new Date(d.getFullYear(),d.getMonth()-1,1));
					const daysInNextMonth = getDaysInNextMonth(d);
					button.addEventListener("keydown",(e) => {
						let newDate;
						switch(e.key){
						case "Home":
							newDate = new Date(d.getFullYear(),d.getMonth(),1);
							break;
						case "End":
							newDate = new Date(d.getFullYear(),d.getMonth()+1,0);
							break;
						case "ArrowUp":
							newDate = new Date(d.getFullYear(),d.getMonth(),d.getDate()-7);
							this.setFocusDateAndFocus(newDate);
							e.preventDefault();
							break;
						case "ArrowDown":
							newDate = new Date(d.getFullYear(),d.getMonth(),d.getDate()+7);
							this.setFocusDateAndFocus(newDate);
							e.preventDefault();
							break;
						case "ArrowLeft":
							if(d.getDate() === 1){
								newDate = new Date(d.getFullYear(),d.getMonth()-1,7 - prevMonthPadding);
							}else if(columnNo === 0){
								const shiftDate = ((rowNo * 7) + columnNo) - prevMonthPadding;
								newDate = new Date(d.getFullYear(),d.getMonth()-1,shiftDate);
								if(newDate.getMonth() === d.getMonth() ){ //padding might throw this off
									newDate = new Date(d.getFullYear(),d.getMonth(), 0);
								}
							}else{
								newDate = new Date(d.getFullYear(),d.getMonth(),d.getDate()-1);
							}
							this.setFocusDateAndFocus(newDate);
							e.preventDefault();
							break;
						case "ArrowRight":
							if(columnNo === 6){
								if(rowNo === 1){
									newDate = new Date(d.getFullYear(),d.getMonth()+1,1);
								}else{
									let shiftDate = (rowNo * 7) - getMonthPadding(new Date(d.getFullYear(),d.getMonth()+1,1)) - columnNo;
									newDate = new Date(d.getFullYear(),d.getMonth()+1,shiftDate);
								}
							}else if(d.getDate() === getDaysInMonth(d) ){
								const tempDate = new Date(d.getFullYear(),d.getMonth()+1,daysInNextMonth);
								newDate = new Date(tempDate.getFullYear(),tempDate.getMonth(),daysInNextMonth - tempDate.getDay());
							}else{
								newDate = new Date(d.getFullYear(),d.getMonth(),d.getDate()+1);
							}
							this.setFocusDateAndFocus(newDate);
							e.preventDefault();
							break;
						}
					});
					cell.appendChild(button);
					calRow.appendChild(cell);  
				}              
			}
		}
		if(calRow !== null){
			if(calRow.childNodes.length !== 0)
				calTable.appendChild(calRow);
		}
		return calTable;
	}

	/**
     * set the focus date and focus it
     * @param {Date} d 
     */
	setFocusDateAndFocus(d){
		this.focusDate = d;
		const focusCell = this.getFocusDateElement();
		if(focusCell) focusCell.querySelector("button").focus();
	}

}

export default NuCalSingleCalendarView;